import React, { useState, useEffect, useRef } from "react";

function parseDDMMYYYY(str) {
  const parts = String(str).split("/");
  if (parts.length === 3) {
    const [dd, mm, yyyy] = parts.map((s) => parseInt(s, 10));
    if (!isNaN(dd) && !isNaN(mm) && !isNaN(yyyy)) return new Date(yyyy, mm - 1, dd);
  }
  const d = new Date(str);
  return isNaN(d.getTime()) ? new Date() : d;
}

function formatDDMMYYYY(date) {
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const yyyy = date.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

const DatePicker = ({ value, onChange, placeholder = "dd/mm/yyyy" }) => {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value || "");
  const [viewYear, setViewYear] = useState(() => {
    const d = value ? parseDDMMYYYY(value) : new Date();
    return d.getFullYear();
  });
  const [viewMonth, setViewMonth] = useState(() => {
    const d = value ? parseDDMMYYYY(value) : new Date();
    return d.getMonth();
  });
  const ref = useRef();

  useEffect(() => {
    setInputValue(value || "");
  }, [value]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  function handleInputChange(e) {
    setInputValue(e.target.value);
  }

  function handleBlur() {
    const parts = inputValue.split("/");
    if (parts.length === 3) {
      const [dd, mm, yyyy] = parts.map((p) => parseInt(p, 10));
      if (
        !isNaN(dd) && !isNaN(mm) && !isNaN(yyyy) &&
        dd >= 1 && dd <= 31 && mm >= 1 && mm <= 12 && yyyy > 1900
      ) {
        onChange(formatDDMMYYYY(new Date(yyyy, mm - 1, dd)));
        setViewYear(yyyy);
        setViewMonth(mm - 1);
        return;
      }
    }
  }

  const selectedDate = value ? parseDDMMYYYY(value) : null;
  const daysInMonth = (y, m) => new Date(y, m + 1, 0).getDate();
  const firstDayIndex = (y, m) => new Date(y, m, 1).getDay();

  const weeks = [];
  const dim = daysInMonth(viewYear, viewMonth);
  let week = Array(7).fill(null);
  let dayCounter = 1;
  const startIndex = firstDayIndex(viewYear, viewMonth);
  for (let i = 0; i < startIndex; i++) week[i] = null;
  for (let i = startIndex; i < 7; i++) {
    week[i] = dayCounter++;
  }
  weeks.push(week);
  while (dayCounter <= dim) {
    const wk = Array(7).fill(null);
    for (let i = 0; i < 7 && dayCounter <= dim; i++) wk[i] = dayCounter++;
    weeks.push(wk);
  }

  return (
    <div className="relative" ref={ref}>
      <div className="flex items-center">
        <input
          type="text"
          className="shadow-sm appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-yellow-500"
          placeholder={placeholder}
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => setOpen(true)}
          onBlur={handleBlur}
        />
        <button
          type="button"
          onClick={() => setOpen((s) => !s)}
          className="ml-2 p-2 bg-yellow-100 rounded border hover:bg-yellow-200"
          aria-label="Toggle calendar"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3M3 11h18M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </button>
      </div>
      {open && (
        <div className="absolute z-50 mt-2 w-64 bg-white border rounded shadow-lg p-3 text-sm text-gray-800">
          <div className="flex items-center justify-between mb-2">
            <button type="button" onClick={() => { if (viewMonth === 0) { setViewMonth(11); setViewYear((y) => y - 1); } else setViewMonth((m) => m - 1); }} className="p-1 hover:bg-gray-100 rounded">‹</button>
            <div className="font-semibold">
              {new Date(viewYear, viewMonth).toLocaleString("default", { month: "long" })} {viewYear}
            </div>
            <button type="button" onClick={() => { if (viewMonth === 11) { setViewMonth(0); setViewYear((y) => y + 1); } else setViewMonth((m) => m + 1); }} className="p-1 hover:bg-gray-100 rounded">›</button>
          </div>
          <div className="grid grid-cols-7 gap-1 text-center text-xs text-gray-500">
            {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d => (
              <div key={d} className="font-medium">{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1 mt-2">
            {weeks.flat().map((d, idx) => {
              const isSelected = d && selectedDate && d === selectedDate.getDate() && viewMonth === selectedDate.getMonth() && viewYear === selectedDate.getFullYear();
              return (
                <button
                  key={idx}
                  type="button"
                  onClick={() => {
                    if (!d) return;
                    const picked = new Date(viewYear, viewMonth, d);
                    const formatted = formatDDMMYYYY(picked);
                    setInputValue(formatted);
                    onChange(formatted);
                    setOpen(false);
                  }}
                  className={`p-1 rounded ${d ? 'hover:bg-gray-100' : ''} ${isSelected ? 'bg-red-500 text-white' : 'text-gray-700'}`}
                  disabled={!d}
                >
                  {d || ''}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default DatePicker;
